import { useEffect, useState } from "react";
import { Boxes, FileText, PackageCheck, Plus, RotateCcw, Send, Trash2, Undo2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShipmentStatus } from "../components/aset/ShipmentStatus";
import { ShipmentItemsTable } from "../components/aset/ShipmentItemsTable";
import { CreateShipmentDialog } from "../components/aset/CreateShipmentDialog";
import { PendingUsageCard } from "../components/aset/PendingUsageCard";
import { UsageDialog } from "../components/aset/UsageDialog";
import { AssetUsageList } from "../components/aset/AssetUsageList";
import { KtStockTable } from "../components/aset/KtStockTable";
import { AssetSummaryCard } from "../components/aset/AssetSummaryCard";
import { AssetExportButton } from "../components/aset/AssetExportButton";
import { ReturnDialog, AssetReturnList } from "../components/aset/AssetReturns";
import { AssetProductsCard } from "../components/aset/AssetProductsCard";
import { ReorderDialog, AssetRequestList } from "../components/aset/AssetRequests";

function ShipmentCard({ s, isAdmin, me, onKirim, onTerima, onDelete }) {
  const [openDetail, setOpenDetail] = useState(s.status !== "diterima");
  const [pdfBusy, setPdfBusy] = useState(false);
  const suratJalan = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/asset-shipments/${s.id}/surat-jalan.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank") || Object.assign(document.createElement("a"), { href: url, download: `surat-jalan-${s.code}.pdf` }).click();
    } catch (e) { toast.error(errMsg(e)); } finally { setPdfBusy(false); }
  };
  return (
    <Card className="bg-card border-border overflow-hidden" data-testid={`shipment-${s.id}`}>
      <CardContent className="pt-4 space-y-2 overflow-x-auto">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className="font-heading font-bold text-sm">{s.code} <span className="text-muted-foreground font-normal">→ {s.kt_name}</span></div>
            <div className="text-[10px] text-muted-foreground">Disiapkan {fmtDateTime(s.created_at)} oleh {s.created_by_name}{s.note && ` · ${s.note}`}</div>
          </div>
          <div className="text-right"><div className="font-mono font-bold text-sm" data-testid={`shipment-total-${s.id}`}>{rupiah(s.total)}</div><div className="text-[10px] text-muted-foreground">{s.items.length} jenis · {s.items.reduce((a, i) => a + i.qty, 0)} unit</div></div>
        </div>
        <ShipmentStatus status={s.status} receivedBy={s.received_by} testid={`shipment-status-${s.id}`} />
        <button type="button" className="text-[11px] text-sky-500 hover:underline" onClick={() => setOpenDetail(!openDetail)} data-testid={`shipment-toggle-${s.id}`}>{openDetail ? "Sembunyikan rincian" : "Lihat rincian barang"}</button>
        {openDetail && <ShipmentItemsTable items={s.items} total={s.total} testid={`shipment-items-${s.id}`} />}
        <div className="flex gap-2 flex-wrap pt-1">
          <Button size="sm" variant="outline" className="h-8" onClick={suratJalan} disabled={pdfBusy} data-testid={`shipment-surat-jalan-${s.id}`}><FileText className="w-3.5 h-3.5 mr-1" /> {pdfBusy ? "Menyiapkan..." : "Surat Jalan PDF"}</Button>
          {isAdmin && s.status === "disiapkan" && <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => onKirim(s)} data-testid={`shipment-kirim-${s.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Tandai Dikirim</Button>}
          {!["admin", "admin_fasnet"].includes(me?.role) && s.kt_user_id === me?.id && s.status === "dikirim" && <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onTerima(s)} data-testid={`shipment-terima-${s.id}`}><PackageCheck className="w-3.5 h-3.5 mr-1" /> Konfirmasi Diterima</Button>}
          {isAdmin && s.status === "dikirim" && <Button size="sm" variant="outline" className="h-8 border-emerald-500/40 text-emerald-500" onClick={() => onTerima(s)} data-testid={`shipment-terima-admin-${s.id}`}><PackageCheck className="w-3.5 h-3.5 mr-1" /> Konfirmasi Diterima (Admin)</Button>}
          {me?.role === "admin" && s.status !== "diterima" && <Button size="sm" variant="ghost" className="h-8 text-rose-400" onClick={() => onDelete(s)} data-testid={`shipment-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Aset() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const isKt = !isAdmin;
  const [ships, setShips] = useState([]);
  const [users, setUsers] = useState([]);
  const [stock, setStock] = useState(null);
  const [ktView, setKtView] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [useOpen, setUseOpen] = useState(false);
  const [retOpen, setRetOpen] = useState(false);
  const [reorder, setReorder] = useState(null); // {open, preset}
  const [shipPreset, setShipPreset] = useState(null);
  const [products, setProducts] = useState([]);
  const [tick, setTick] = useState(0);
  const [usePreset, setUsePreset] = useState("");
  const [pin, setPin] = useState(null); // {type, s}

  const load = () => {
    setTick((t) => t + 1);
    api.get("/asset-products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/asset-shipments").then((r) => setShips(r.data)).catch((e) => toast.error(errMsg(e)));
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (isKt) api.get("/asset-stock/me").then((r) => setStock(r.data)).catch(() => setStock([]));
  };
  useEffect(() => { load(); }, [isAdmin, isKt]); // eslint-disable-line
  const ktList = users.filter((u) => !["admin", "admin_fasnet"].includes(u.role)).map((u) => ({ ...u, name: `${u.name || u.email}${u.agent_code ? ` (${u.agent_code})` : ""}${u.role ? ` — ${u.role.replace(/_/g, " ")}` : ""}` }));
  useEffect(() => {
    if (!isAdmin || !ktView) return;
    api.get(`/asset-stock/${ktView}`).then((r) => setStock(r.data)).catch(() => setStock([]));
  }, [ktView, isAdmin, ships]);

  const run = async () => {
    const { type, s } = pin;
    try {
      if (type === "kirim") { await api.post(`/asset-shipments/${s.id}/kirim`); toast.success(`${s.code} ditandai dikirim — notifikasi ke ${s.kt_name}`); }
      if (type === "terima") { await api.post(`/asset-shipments/${s.id}/terima`); toast.success(isAdmin ? `${s.code} dikonfirmasi diterima oleh ${s.kt_name} (cek lapangan) — stok penerima bertambah` : `${s.code} diterima — stok Anda bertambah`); }
      if (type === "hapus") { await api.delete(`/asset-shipments/${s.id}`); toast.success(`${s.code} dihapus`); }
      setPin(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const open = ships.filter((s) => s.status !== "diterima");
  const done = ships.filter((s) => s.status === "diterima");

  return (
    <div className="space-y-6" data-testid="aset-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Boxes className="w-7 h-7 text-sky-500" /> Aset</h1>
          <p className="text-sm text-muted-foreground mt-1">{isKt ? "Kiriman barang/alat dari admin untuk Anda. Konfirmasi saat diterima, lalu catat setiap alat yang dipasang." : "Kirim barang & alat ke agen/teknisi (semua level): disiapkan → dikirim → diterima."}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && <AssetExportButton />}
          {isAdmin && <Button onClick={() => setCreateOpen(true)} data-testid="btn-create-shipment"><Plus className="w-4 h-4 mr-1.5" /> Kirim Aset</Button>}
          {isKt && <Button variant="outline" className="text-sky-400 border-sky-500/40" onClick={() => setReorder({ open: true, preset: null })} data-testid="btn-reorder"><RotateCcw className="w-4 h-4 mr-1.5" /> Pesan Barang</Button>}
          {isKt && <Button variant="outline" className="text-rose-500 border-rose-500/40" onClick={() => setRetOpen(true)} data-testid="btn-return-asset"><Undo2 className="w-4 h-4 mr-1.5" /> Retur Rusak</Button>}
          {isKt && <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => { setUsePreset(""); setUseOpen(true); }} data-testid="btn-use-asset"><Wrench className="w-4 h-4 mr-1.5" /> Pasang / Gunakan</Button>}
        </div>
      </div>

      <AssetSummaryCard refreshKey={tick} />
      {isAdmin && <PendingUsageCard refreshKey={tick} onChanged={load} />}
      <AssetRequestList refreshKey={tick} onFulfil={(r) => { setShipPreset({ kt_user_id: r.kt_user_id, items: r.items, note: r.note ? `Permintaan: ${r.note}` : "Dari permintaan barang", request_id: r.id }); setCreateOpen(true); }} />
      <AssetProductsCard refreshKey={tick} onReorder={isKt ? (p) => setReorder({ open: true, preset: p }) : undefined} onShip={isAdmin ? (p) => { setShipPreset({ items: [{ name: p.name, qty: 1 }] }); setCreateOpen(true); } : undefined} />
      <AssetReturnList refreshKey={tick} onChanged={load} />

      {isKt && (
        <Card className="bg-card border-border" data-testid="kt-stock-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Stok Aset Saya</CardTitle></CardHeader>
          <CardContent><KtStockTable stock={stock} onUse={(n) => { setUsePreset(n); setUseOpen(true); }} /></CardContent>
        </Card>
      )}

      {isAdmin && (
        <Card className="bg-card border-border" data-testid="admin-kt-stock-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base font-heading">Stok di Agen / Teknisi</CardTitle>
              <Select value={ktView} onValueChange={setKtView}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid="select-kt-view"><SelectValue placeholder="Pilih agen/teknisi..." /></SelectTrigger>
                <SelectContent>{ktList.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`kt-view-opt-${u.id}`}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>{ktView ? <KtStockTable stock={stock} testid="admin-kt-stock" /> : <div className="text-xs text-muted-foreground py-3 text-center">Pilih agen/teknisi untuk melihat sisa stok alatnya</div>}</CardContent>
        </Card>
      )}

      <div className="space-y-2">
        <h2 className="text-base md:text-lg font-heading font-semibold">Kiriman Berjalan <span className="text-xs text-muted-foreground font-normal">({open.length})</span></h2>
        {!open.length && <div className="text-xs text-muted-foreground border border-dashed border-border rounded-xl py-6 text-center" data-testid="shipments-open-empty">Tidak ada kiriman yang sedang berjalan</div>}
        <div className="grid gap-3 md:grid-cols-2">{open.map((s) => <ShipmentCard key={s.id} s={s} isAdmin={isAdmin} me={me} onKirim={(x) => setPin({ type: "kirim", s: x })} onTerima={(x) => setPin({ type: "terima", s: x })} onDelete={(x) => setPin({ type: "hapus", s: x })} />)}</div>
      </div>

      {!!done.length && (
        <div className="space-y-2">
          <h2 className="text-base md:text-lg font-heading font-semibold">Riwayat Diterima <span className="text-xs text-muted-foreground font-normal">({done.length})</span></h2>
          <div className="grid gap-3 md:grid-cols-2">{done.map((s) => <ShipmentCard key={s.id} s={s} isAdmin={isAdmin} me={me} />)}</div>
        </div>
      )}

      <AssetUsageList key={tick} ktUserId={isKt ? me.id : ""} compact onChanged={load} />

      {isAdmin && <CreateShipmentDialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setShipPreset(null); }} ktList={ktList} onDone={load} preset={shipPreset} products={products} />}
      {isKt && <ReorderDialog open={!!reorder?.open} onOpenChange={(o) => !o && setReorder(null)} preset={reorder?.preset} products={products} onDone={load} />}
      {isKt && useOpen && <UsageDialog open={useOpen} onOpenChange={setUseOpen} stock={stock || []} preset={usePreset} onDone={load} />}
      {isKt && <ReturnDialog open={retOpen} onOpenChange={setRetOpen} stock={stock || []} onDone={load} />}
      <PinConfirm open={!!pin} onOpenChange={() => setPin(null)} destructive={pin?.type === "hapus"}
        title={pin?.type === "kirim" ? "Tandai Dikirim?" : pin?.type === "terima" ? "Konfirmasi Barang Diterima?" : "Hapus Kiriman?"}
        description={pin ? `${pin.s.code} — ${pin.s.items.length} jenis barang, total ${rupiah(pin.s.total)}${pin.type === "terima" && isAdmin ? `. Anda mengonfirmasi atas nama ${pin.s.kt_name} berdasarkan cek lapangan (penerima belum klik terima). Stok aset ${pin.s.kt_name} akan bertambah dan ia mendapat notifikasi.` : ""}` : ""}
        confirmLabel={pin?.type === "kirim" ? "Ya, Dikirim" : pin?.type === "terima" ? "Ya, Sudah Diterima" : "Ya, Hapus"} onConfirm={run} testid="pin-shipment" />
    </div>
  );
}
